import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Save, Sparkles, ChevronDown, ChevronUp,
  Users as UsersIcon, Eye, FileText, Brain, AlertCircle, Lightbulb, Wrench
} from 'lucide-react';

const SUMMARY_SUBSECTIONS = [
  { group: 'Evaluators & Environment', items: [
    { key: 'evaluators', label: 'Evaluator(s) Who Completed Additional Assessments', icon: UsersIcon, hint: 'List the evaluators and their credentials.' },
    { key: 'testing_environment', label: 'Observation: Testing Environment', icon: Eye, hint: 'Describe the testing conditions and student behavior during evaluation.' },
  ]},
  { group: 'Analysis', items: [
    { key: 'interpretation_of_results', label: 'Interpretation of Assessment Results', icon: Brain, hint: 'Detailed interpretation of all assessment results.' },
    { key: 'summary', label: 'Summary', icon: FileText, hint: 'Overall summary of evaluation findings.' },
    { key: 'conclusion', label: 'Conclusion', icon: FileText, hint: 'Conclusions drawn from the evaluation data.' },
  ]},
  { group: 'Implications & Recommendations', items: [
    { key: 'educational_implications', label: 'Educational Implications of Suspected Disability', icon: AlertCircle, hint: 'Document how the suspected disability impacts educational performance.', ai: true },
    { key: 'service_modifications', label: 'Consideration of Additions or Modifications to Services', icon: Wrench, hint: 'What services may need to be added or changed?' },
    { key: 'assistive_technology', label: 'Assistive Technology', icon: Wrench, hint: 'Assistive technology needs and recommendations.' },
    { key: 'recommendations', label: 'Recommendations and Identification of Needs', icon: Lightbulb, hint: 'Specific recommendations for academic and behavioral needs.' },
  ]},
];

function mergeContent(existing) {
  const defaults = {
    evaluators: '', testing_environment: '', interpretation_of_results: '',
    summary: '', conclusion: '', educational_implications: '',
    service_modifications: '',
    potential_eligibility: { statement: '', categories: [] },
    assistive_technology: '', recommendations: '',
  };
  if (!existing || typeof existing !== 'object') return defaults;
  if (existing.notes && !existing.evaluators && !existing.summary) {
    return { ...defaults, summary: existing.notes };
  }
  return {
    ...defaults, ...existing,
    potential_eligibility: { ...defaults.potential_eligibility, ...(existing.potential_eligibility || {}) },
  };
}

export default function EvaluationSummaryEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));
  const [collapsed, setCollapsed] = useState({});
  useEffect(() => { setContent(mergeContent(section?.content)); }, [section?.section_id]);

  const updateField = (key, value) => setContent(prev => ({ ...prev, [key]: value }));
  const handleSave = () => onSave(content);

  const allKeys = SUMMARY_SUBSECTIONS.flatMap(g => g.items.map(i => i.key));
  const filledCount = allKeys.filter(k => (content[k] || '').trim()).length;

  return (
    <div className="max-w-4xl space-y-8" data-testid="eval-summary-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="eval-summary-title">Evaluation Summary</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <Badge className="bg-[#5A8A5A]/10 text-[#5A8A5A] text-xs">{filledCount}/{allKeys.length} sections completed</Badge>
            {section?.ai_capable && <Badge className="bg-amber-50 text-amber-700 text-xs"><Sparkles className="h-3 w-3 mr-1" />AI-Assisted</Badge>}
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl shrink-0" data-testid="eval-summary-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save All'}
        </Button>
      </div>

      {SUMMARY_SUBSECTIONS.map(group => (
        <div key={group.group}>
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-3 px-1">{group.group}</h3>
          <div className="space-y-3">
            {group.items.map(sub => {
              const value = content[sub.key] || '';
              const isCollapsed = collapsed[sub.key];
              const Icon = sub.icon;
              return (
                <div key={sub.key} className="bg-white border border-[#E5E5E5] rounded-2xl" data-testid={`summary-card-${sub.key}`}>
                  <div className="flex items-center justify-between px-5 py-3.5 cursor-pointer" onClick={() => setCollapsed(prev => ({ ...prev, [sub.key]: !prev[sub.key] }))}>
                    <div className="flex items-center gap-3">
                      <Icon className="h-4 w-4 text-[#5A8A5A] shrink-0" />
                      <span className="font-medium text-sm text-[#1C1C1C]">{sub.label}</span>
                      {sub.ai && <Sparkles className="h-3 w-3 text-amber-500 shrink-0" />}
                      {value.trim() && <span className="text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Has content</span>}
                    </div>
                    {isCollapsed ? <ChevronDown className="h-4 w-4 text-[#595959]" /> : <ChevronUp className="h-4 w-4 text-[#595959]" />}
                  </div>
                  {!isCollapsed && (
                    <div className="px-5 pb-5 border-t border-[#F3F4F1]">
                      <p className="text-xs text-[#595959] mt-3 mb-3">{sub.hint}</p>
                      <Textarea value={value} onChange={e => updateField(sub.key, e.target.value)}
                        placeholder={sub.hint} rows={5} className="rounded-xl border-[#E5E5E5] text-sm"
                        data-testid={`summary-input-${sub.key}`}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Potential Eligibility - special structured section */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-3 px-1">Potential Eligibility</h3>
        <div className="bg-white border border-amber-200 rounded-2xl p-5" data-testid="potential-eligibility-section">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <span className="font-medium text-sm text-[#1C1C1C]">Statement of Potential Eligibility for Special Education</span>
          </div>
          <p className="text-xs text-amber-700 mb-3 italic">Advisory only — final determination is made by the evaluation team.</p>
          <Textarea value={content.potential_eligibility.statement} onChange={e => setContent(prev => ({
            ...prev, potential_eligibility: { ...prev.potential_eligibility, statement: e.target.value },
          }))} placeholder="Provide a statement regarding potential eligibility..." rows={4}
            className="rounded-xl border-[#E5E5E5] text-sm" data-testid="eligibility-statement"
          />
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl" data-testid="eval-summary-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Evaluation Summary'}
        </Button>
      </div>
    </div>
  );
}
